import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { get } from 'lodash';
import React, { FC } from 'react';
import theme from 'src/assets/theme/theme';
import { useApplication } from 'src/context/ApplicationContext';
import { KOSTEN_ARTEN, KOSTENTRAEGER } from 'src/lib/constants/constant';
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
import KostenpositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
import { DisplayEuro } from 'src/utils/formats';

const P = AntragSchema.attributes;
const KP_P = KostenpositionSchema.attributes;
const LABEL_COLUMN_WIDTH = '42%';

const FormantragKostenTabelle: FC = () => {
	const { application } = useApplication();
	const allKP: any[] = get(application, P.Foerderantrag_Kostenpositions.path, []);

	const ausgabeKP = allKP.filter((kp) => get(kp, KP_P.Allgemein_Kostenposition.path) === 'Ausgabe');

	//  unique projektjahre, sorted ascending
	const years: number[] = Array.from(
		new Set(allKP.flatMap((kp) => get(kp, KP_P.Allgemein_Kosten_Pro_Jahr.path, []).map((k: any) => Number(k.Zelle_1)))),
	)
		.filter((y) => !isNaN(y))
		.sort((a, b) => a - b);

	const getYearlySum = (kps: any[], year: number): number =>
		kps.reduce((total, kostenPosition) => {
			const yearEntry = get(kostenPosition, KP_P.Allgemein_Kosten_Pro_Jahr.path, []).find((k: any) => Number(k.Zelle_1) === year);
			return total + (parseFloat(get(yearEntry, 'Zelle_2', '0')) || 0);
		}, 0);

	// Personalausgaben: nur Antragsteller's Personalkosten
	const personalKP = ausgabeKP.filter(
		(kp) =>
			get(kp, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Personalkosten &&
			get(kp, KP_P.Allgemein_Kostentraeger.path) === KOSTENTRAEGER.Antragsteller,
	);

	// Sachausgaben + Personalausgaben der Verbundpartner
	const sachKP = ausgabeKP.filter((kp) => {
		const kostenart = get(kp, KP_P.Allgemein_Kostenart_Finanzierung.path);
		const kostentraeger = get(kp, KP_P.Allgemein_Kostentraeger.path);

		const isSachkosten = kostenart === KOSTEN_ARTEN.Sachkosten;
		const isPartnerPersonalkosten = kostenart === KOSTEN_ARTEN.Personalkosten && kostentraeger === KOSTENTRAEGER.Verbundpartner;

		return isSachkosten || isPartnerPersonalkosten;
	});

	const personalSums = years.map((y) => getYearlySum(personalKP, y));
	const sachSums = years.map((y) => getYearlySum(sachKP, y));
	const gesamtSums = years.map((_, i) => personalSums[i] + sachSums[i]);

	const personalTotal = personalSums.reduce((total, yearAmount) => total + yearAmount, 0);
	const sachTotal = sachSums.reduce((total, yearAmount) => total + yearAmount, 0);
	const gesamtTotal = personalTotal + sachTotal;

	return (
		<TableContainer>
			<Table size='medium' aria-label='a dense table' sx={{ tableLayout: 'fixed', width: '100%' }}>
				<TableHead>
					<TableRow>
						<TableCell sx={{ backgroundColor: theme.palette.background.paper, width: LABEL_COLUMN_WIDTH }}>Euro</TableCell>
						{years.map((y) => (
							<TableCell key={y} align='right' sx={{ backgroundColor: theme.palette.background.paper }}>
								{y}
							</TableCell>
						))}
						<TableCell align='right' sx={{ backgroundColor: theme.palette.background.paper }}>
							Gesamt
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					<TableRow>
						<TableCell sx={{ width: LABEL_COLUMN_WIDTH }}>Personalausgaben (nur Antragsteller/in)</TableCell>
						{personalSums.map((val, i) => (
							<TableCell key={i} align='right'>
								{DisplayEuro(parseFloat(val.toFixed(2)))}
							</TableCell>
						))}
						<TableCell align='right'>{DisplayEuro(personalTotal)}</TableCell>
					</TableRow>
					<TableRow>
						<TableCell sx={{ width: LABEL_COLUMN_WIDTH }}>
							Summe Sachausgaben (d.h. Sachausgaben Antragsteller/in und alle Ausgaben der Partner)
						</TableCell>
						{sachSums.map((val, i) => (
							<TableCell key={i} align='right'>
								{DisplayEuro(parseFloat(val.toFixed(2)))}
							</TableCell>
						))}
						<TableCell align='right'>{DisplayEuro(sachTotal)}</TableCell>
					</TableRow>
					<TableRow className='total-row'>
						<TableCell sx={{ width: LABEL_COLUMN_WIDTH }}>Gesamtausgaben</TableCell>
						{gesamtSums.map((val, i) => (
							<TableCell key={i} align='right'>
								{DisplayEuro(parseFloat(val.toFixed(2)))}
							</TableCell>
						))}
						<TableCell align='right'>{DisplayEuro(gesamtTotal)}</TableCell>
					</TableRow>
				</TableBody>
			</Table>
		</TableContainer>
	);
};

export default FormantragKostenTabelle;
